import i18n from '@/i18n';
import { aiImageFissionPromptCaseAPI } from '@/utils/api';
import {
  DEFAULT_TITLE_PRESET_SCOPE,
  normalizeTitlePresetScope,
  resolveVisibleTitlePresetScopes,
} from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import {
  buildText2ImgCaseOwnerFilterOptions,
  resolveText2ImgCaseOwnerLabel,
} from '@/pages/ipe/workbench/utils/text2imgPromptCaseLoader';
import { IMAGE_FISSION_VARIATION_COUNT_OPTIONS } from '@/pages/ipe/workbench/constants/imageFissionStudioConfig';

export const IMAGE_FISSION_CASE_DEFAULT_PAGE_SIZE = 20;
export const IMAGE_FISSION_CASE_PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

export function formatImageFissionCaseSummary(item) {
  if (!item) return '';
  const ratio = String(item.aspectRatio || 'original');
  const w = Number(item.outputWidth) || 0;
  const h = Number(item.outputHeight) || 0;
  const sizePart = ratio === 'original'
    ? '原图比例'
    : `${ratio}${w > 0 && h > 0 ? ` · ${w}×${h}` : ''}`;
  const patternPart = item.patternOnly ? '仅裂变图案' : '全图裂变';
  const count = IMAGE_FISSION_VARIATION_COUNT_OPTIONS.includes(Number(item.variationCount))
    ? Number(item.variationCount)
    : 1;
  return i18n.t('workbench:ipeUtilError.fissionStyleCount', { size: sizePart, pattern: patternPart, count });
}

/** 将后端 CaseView 转为裂变模板库统一结构（fallbackScope：后端未返回 caseScope 时的兜底） */
export function mapImageFissionPromptCaseApiItem(item, fallbackScope = 'official') {
  if (!item || typeof item !== 'object') return null;
  const name = String(item.name || '').trim() || '未命名模板';
  const id = item.id != null ? String(item.id) : '';
  if (!id) return null;
  const status = item.status != null ? Number(item.status) : 1;
  if (status === 0) return null;
  const aspectRatio = String(item.aspectRatio || '2:3').trim() || '2:3';
  const outputWidth = Math.max(0, Math.floor(Number(item.outputWidth) || 0));
  const outputHeight = Math.max(0, Math.floor(Number(item.outputHeight) || 0));
  const patternOnly = item.patternOnly === true || Number(item.patternOnly) === 1;
  const variationCount = IMAGE_FISSION_VARIATION_COUNT_OPTIONS.includes(Number(item.variationCount))
    ? Number(item.variationCount)
    : 1;
  const mapped = {
    id,
    name,
    aspectRatio,
    outputWidth,
    outputHeight,
    patternOnly,
    variationCount,
    scope: normalizeTitlePresetScope(item.caseScope || item.scope || fallbackScope),
    canEdit: Boolean(item.canEdit),
    canDelete: Boolean(item.canDelete),
    ownerUserId: item.ownerUserId ?? null,
    ownerDisplayName: String(item.ownerDisplayName || '').trim(),
    sortWeight: Number(item.sortWeight) || 0,
    source: 'api',
  };
  mapped.summary = formatImageFissionCaseSummary(mapped);
  return mapped;
}

/**
 * 按 scope 真分页拉取裂变模板（个人 / 团队 / 官方）。
 * includeOfficial=true 时后端会把「官方」合并进本次查询（前端模板库锁定作用域时使用）。
 */
export async function loadImageFissionPromptCasesPage({
  scope = DEFAULT_TITLE_PRESET_SCOPE,
  keyword = '',
  ownerUserId = '',
  page = 1,
  pageSize = IMAGE_FISSION_CASE_DEFAULT_PAGE_SIZE,
  includeOfficial = false,
} = {}) {
  const safeScope = normalizeTitlePresetScope(scope);
  const ownerId = ownerUserId != null && String(ownerUserId).trim() !== ''
    ? String(ownerUserId).trim()
    : undefined;
  const res = await aiImageFissionPromptCaseAPI.list({
    scope: safeScope,
    keyword: String(keyword || '').trim() || undefined,
    ownerUserId: safeScope === 'team' ? ownerId : undefined,
    page,
    pageSize,
    includeOfficial: includeOfficial ? true : undefined,
  });
  const items = (Array.isArray(res?.items) ? res.items : [])
    .map((raw) => mapImageFissionPromptCaseApiItem(raw, safeScope))
    .filter(Boolean);
  return {
    items,
    total: Number(res?.total) || 0,
    page: Number(res?.page) || page || 1,
    pageSize: Number(res?.pageSize) || pageSize || IMAGE_FISSION_CASE_DEFAULT_PAGE_SIZE,
    caseScope: res?.caseScope || safeScope,
  };
}

export const resolveImageFissionCaseOwnerLabel = resolveText2ImgCaseOwnerLabel;
export const buildImageFissionCaseOwnerFilterOptions = buildText2ImgCaseOwnerFilterOptions;

export { resolveVisibleTitlePresetScopes, DEFAULT_TITLE_PRESET_SCOPE };

export function applyImageFissionCaseToStudioParams(item) {
  if (!item) return null;
  const aspectRatio = String(item.aspectRatio || '2:3');
  if (aspectRatio === 'original') {
    return {
      aspectRatio: 'original',
      outputWidth: 0,
      outputHeight: 0,
      patternOnly: Boolean(item.patternOnly),
      variationCount: Number(item.variationCount) || 1,
    };
  }
  return {
    aspectRatio,
    outputWidth: Math.max(1, Math.floor(Number(item.outputWidth) || 0)),
    outputHeight: Math.max(1, Math.floor(Number(item.outputHeight) || 0)),
    patternOnly: Boolean(item.patternOnly),
    variationCount: Number(item.variationCount) || 1,
  };
}
